// Generatore PDF riassuntivo di una pratica inviata.
// Usa jsPDF + jspdf-autotable. Il PDF NON include gli allegati (libretto,
// identità, ecc.) — solo i dati strutturati della polizza.

import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { formatEuro, VOCI_LABEL, VOCI_BASE, VOCI_PLUS_EXTRA, formatDecorrenza } from './calcoli'

const COLORS = {
  primary: [30, 100, 180],   // azure
  accent: [120, 180, 230],   // celeste
  text: [40, 40, 50],
  muted: [110, 110, 120],
  border: [220, 225, 235],
}

const MARGIN = 15
const PAGE_BOTTOM_LIMIT = 270   // sotto questa Y forziamo addPage()

const CAUSALE_LABEL = {
  nuova: 'Nuova emissione',
  usata: 'Veicolo usato',
  rinnovo: 'Rinnovo',
}

const TIPO_OP_LABEL = {
  concessionari: 'Concessionari',
  annuali: 'Annuali',
  autocarri_intermediari: 'Polizze autocarri',
  solo_standalone: 'Solo Stand-alone',
}

// Label corti per i fattori della polizza (Dettaglio premio).
// Allineati a VOCI_LABEL ma più sintetici dove serve.
const FATTORE_LABEL = {
  Fire: 'Incendio',
  Theft: 'Furto',
  Guarantees_Third_Party_Recourse: 'Ricorso terzi',
  Driver_Personal_Accident: 'Infortunio conducente',
  Vandalism: 'Atti vandalici',
  Natural_Perils: 'Eventi atmosferici',
  Glass: 'Cristalli',
  Collision: 'Collisione',
  Total_Kasko: 'Kasko totale',
  Animal_Collision: 'Urto con animali',
  VanExtra: 'Valore a nuovo esteso',
}

function fmtDate(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch { return iso }
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return iso }
}

function siNo(v) { return v ? 'Sì' : 'No' }
function val(v) { return v == null || v === '' ? '—' : String(v) }

// Carica il logo (PNG da /public) come data URL utilizzabile da jsPDF.addImage
async function caricaLogoDataUrl() {
  try {
    const url = `${process.env.PUBLIC_URL || ''}/logo-diemmeci.png`
    const res = await fetch(url)
    if (!res.ok) return null
    const blob = await res.blob()
    return await new Promise((resolve) => {
      const fr = new FileReader()
      fr.onload = () => resolve(fr.result)
      fr.onerror = () => resolve(null)
      fr.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

function disegnaHeader(doc, logoDataUrl, numeroPratica) {
  const pageWidth = doc.internal.pageSize.getWidth()

  if (logoDataUrl) {
    try {
      const w = 38, h = w / 1.6
      doc.addImage(logoDataUrl, 'PNG', MARGIN, 12, w, h)
    } catch { /* PNG non caricabile: ignora */ }
  }

  doc.setTextColor(...COLORS.primary)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('Riepilogo pratica', pageWidth - MARGIN, 20, { align: 'right' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(...COLORS.muted)
  if (numeroPratica) {
    doc.text(`N. ${numeroPratica}`, pageWidth - MARGIN, 27, { align: 'right' })
  }
  doc.text(`Emesso il ${fmtDateTime(new Date().toISOString())}`, pageWidth - MARGIN, 33, { align: 'right' })

  doc.setDrawColor(...COLORS.border)
  doc.setLineWidth(0.4)
  doc.line(MARGIN, 40, pageWidth - MARGIN, 40)
}

function disegnaFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const totale = doc.internal.getNumberOfPages()

  for (let i = 1; i <= totale; i++) {
    doc.setPage(i)
    doc.setDrawColor(...COLORS.border)
    doc.line(MARGIN, pageHeight - 16, pageWidth - MARGIN, pageHeight - 16)
    // Dicitura legale obbligatoria, centrata
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(...COLORS.muted)
    doc.text(
      'Partita IVA 04034751208 - Iscrizione RUI B000714654 - Società soggetta alla vigilanza Ivass',
      pageWidth / 2, pageHeight - 9, { align: 'center' }
    )
    doc.setFontSize(8)
    doc.text(`Pagina ${i} / ${totale}`, pageWidth - MARGIN, pageHeight - 9, { align: 'right' })
  }
}

// Garantisce che ci sia spazio sufficiente sotto la Y corrente.
// Se siamo troppo in basso aggiunge una pagina e ridisegna l'header.
// Ritorna la nuova Y di partenza.
function ensureSpace(doc, y, needed, ctx) {
  if (y + needed <= PAGE_BOTTOM_LIMIT) return y
  doc.addPage()
  disegnaHeader(doc, ctx.logoDataUrl, ctx.numeroPratica)
  return 48
}

// Disegna il titolo di una sezione e restituisce la Y successiva utile.
function sezione(doc, titolo, y, ctx) {
  y = ensureSpace(doc, y, 14, ctx)
  doc.setFillColor(...COLORS.primary)
  doc.rect(MARGIN, y, 3, 6, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.setTextColor(...COLORS.primary)
  doc.text(titolo, MARGIN + 6, y + 5)
  return y + 9
}

function tabellaCoppie(doc, rows, startY, ctx) {
  // autoTable gestisce automaticamente il page break interno, ma se la prima
  // riga finisce a cavallo del fondo pagina può sembrare brutto. Per sicurezza
  // pre-stimiamo lo spazio necessario.
  startY = ensureSpace(doc, startY, Math.min(rows.length * 6 + 4, 30), ctx)
  autoTable(doc, {
    startY,
    head: [],
    body: rows.map(([k, v]) => [k, v]),
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 10,
      textColor: COLORS.text,
      cellPadding: { top: 1.5, bottom: 1.5, left: 0, right: 4 },
    },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: COLORS.muted, cellWidth: 55 },
      1: { textColor: COLORS.text },
    },
    margin: { left: MARGIN + 6, right: MARGIN },
    didDrawPage: (_data) => {
      // Se autoTable apre una nuova pagina, ridisegna l'header
      if (doc.internal.getCurrentPageInfo().pageNumber > 1 && _data.cursor.y < 45) {
        disegnaHeader(doc, ctx.logoDataUrl, ctx.numeroPratica)
      }
    },
  })
  return doc.lastAutoTable.finalY + 4
}

function rigaCliente(data) {
  const dc = data.dati_comuni || {}
  const isSoc = !!dc.is_societa
  const indirizzoCompleto = [
    dc.indirizzo_residenza,
    dc.cap_residenza,
    dc.citta_residenza,
    dc.provincia_residenza ? `(${dc.provincia_residenza})` : null,
  ].filter(Boolean).join(' ')
  return [
    [isSoc ? 'Ragione sociale' : 'Cognome e nome',
      isSoc ? val(data.cliente_nome) : `${val(data.cliente_cognome)} ${val(data.cliente_nome)}`.trim()],
    isSoc ? ['P.IVA', val(data.cliente_cognome)] : null,
    !isSoc ? ['Codice fiscale', val(dc.codice_fiscale)] : null,
    ['Cellulare', val(dc.cellulare_contraente)],
    ['Email', val(dc.email_contraente)],
    ['Residenza', indirizzoCompleto || '—'],
  ].filter(Boolean)
}

function rigaVeicolo(data) {
  const dc = data.dati_comuni || {}
  const isSoloSA = data.tipo_operazione === 'solo_standalone'
  // Targa, marca, modello e colore sono i dati IDENTIFICATIVI del veicolo:
  // li teniamo sempre in cima.
  const rows = [
    ['Targa', val(dc.targa).toUpperCase()],
    ['Numero telaio (VIN)', val(dc.numero_telaio)],
    ['Marca', val(dc.marca_veicolo)],
    ['Modello', val(dc.modello_veicolo)],
    ['Categoria', dc.categoria_veicolo === 'autocarro' ? 'Autocarro' : 'Auto'],
  ]
  if (!isSoloSA) {
    rows.push(
      ['Colore', val(dc.colore_auto)],
      ['Chilometri', dc.km != null && dc.km !== '' ? `${dc.km} km` : '—'],
      ['Data immatricolazione', fmtDate(dc.immatricolazione)],
      ['Valore veicolo', formatEuro(parseFloat(dc.valore_auto))],
    )
    if (data.tipo_operazione === 'autocarri_intermediari') {
      rows.push(['Peso autocarro', dc.peso_autocarro === 'fino_35' ? 'FINO A 35 QUINTALI' : 'OLTRE I 35 QUINTALI'])
    }
  }
  return rows
}

function rigaPolizza(data) {
  const dc = data.dati_comuni || {}
  const rows = [
    ['Tipo operazione', TIPO_OP_LABEL[data.tipo_operazione] || val(data.tipo_operazione)],
    ['Provincia', val(dc.provincia)],
    ['Durata', `${val(dc.durata_mesi)} mesi`],
    ['Causale', CAUSALE_LABEL[dc.causale] || val(dc.causale)],
    ['Pacchetto', data.tipo_operazione === 'autocarri_intermediari' ? val(dc.pacchetto_autocarro) : val(dc.pacchetto)],
    ['Satellitare', data.tipo_operazione === 'autocarri_intermediari' ? 'Non previsto' : siNo(dc.satellitare) + (dc.satellitare ? ` (${val(dc.sat_marca)} · ${val(dc.sat_seriale)})` : '')],
    ['Satellitare acquistato', data.tipo_operazione === 'autocarri_intermediari' ? 'No' : (dc.acquista_lojack ? `${dc.mesi_lojack || dc.durata_mesi || 12} mesi` : 'No')],
  ]
  if (data.tipo_operazione === 'autocarri_intermediari' && dc.cristalli_massimale) {
    rows.push(['Massimale cristalli', `${dc.cristalli_massimale} euro`])
  }
  if (data.tipo_operazione !== 'autocarri_intermediari' && dc.causale === 'usata' && dc.mesi_valore_a_nuovo) {
    rows.push(['Valore a Nuovo Complessivo', `${dc.mesi_valore_a_nuovo} mesi`])
  }
  // VAN dedicato Quixa/Sogessur: mostrato solo se richiesto esplicitamente e
  // se la compagnia scelta è una delle due (le altre non usano questo campo).
  if (
    data.tipo_operazione !== 'autocarri_intermediari' &&
    dc.mesi_valore_a_nuovo_qs &&
    (data.scelta_compagnia === 'QUIXA' || data.scelta_compagnia === 'SOGESSUR')
  ) {
    rows.push(['Valore a nuovo (Quixa/Sogessur)', `${dc.mesi_valore_a_nuovo_qs} mesi`])
  }
  return rows
}

// Garanzie incluse nel pacchetto BASE, per singola compagnia (schema committente).
const GARANZIE_BASE_PER_COMPAGNIA = {
  LLOYD_PLUS: [
    'Incendio/Furto (Totale/Parziale)',
    'Valore a nuovo',
    'Spese di noleggio di un veicolo',
    'Danni da furto di bagagli trasportati',
    'Collisione con veicolo non assicurato ma identificato',
    'Garanzie accessorie',
    'Tutela legale',
    'Collisione con animali',
  ],
  LLOYD_VOLANTE: [
    'Incendio/Furto (Totale/Parziale)',
    'Valore a nuovo',
    'Spese di noleggio di un veicolo',
    'Danni da furto di bagagli trasportati',
    'Collisione con veicolo non assicurato ma identificato',
    'Garanzie accessorie',
    'Tutela legale',
    'Collisione con animali',
  ],
  EIR: [
    'Incendio/Furto (Totale/Parziale)',
    'Valore a nuovo',
    'Ricorso terzi da incendio',
    'Infortuni del conducente',
    'Garanzie accessorie',
  ],
  COVEA: [
    'Incendio/Furto (Totale/Parziale)',
    'Ricorso terzi da incendio',
    'Cristalli',
    'Valore a nuovo',
    'Garanzie accessorie',
  ],
  QUIXA: [
    'Furto e Incendio (Totale/Parziale)',
  ],
  SOGESSUR: [
    'Furto e Incendio (Totale/Parziale)',
  ],
}

// Garanzie AGGIUNTIVE del pacchetto PLUS (oltre alla BASE), per compagnia.
const GARANZIE_PLUS_EXTRA_PER_COMPAGNIA = {
  LLOYD_PLUS: ['Eventi atmosferici', 'Atti vandalici'],
  LLOYD_VOLANTE: ['Eventi atmosferici', 'Atti vandalici'],
  COVEA: ['Eventi atmosferici', 'Atti vandalici', 'Urto con animali'],
  QUIXA: [
    'Ricorso terzi da incendio',
    'Atti vandalici ed eventi naturali',
    'Urto con animali selvatici',
    'Cristalli',
    'Garanzie accessorie',
  ],
  SOGESSUR: [
    'Ricorso terzi da incendio',
    'Atti vandalici ed eventi naturali',
    'Urto con animali selvatici',
    'Cristalli',
    'Garanzie accessorie',
  ],
  EIR: ['Eventi naturali', 'Eventi sociopolitici', 'Garanzie aggiuntive'],
}

// Elenco delle GARANZIE INCLUSE (non i fattori di calcolo):
//   - garanzie del pacchetto scelto per la compagnia scelta (Base, +Plus)
//   - eventuali garanzie Stand-alone selezionate (per nome prodotto)
// Niente costi, niente broker fee, niente voci tecniche di calcolo.
function elencoGaranzie(data, tariffe) {
  const dc = data.dati_comuni || {}
  const cod = data.scelta_compagnia
  if (data.tipo_operazione === 'autocarri_intermediari') {
    if (dc.pacchetto_autocarro === 'ECONOMICO') {
      return ['Furto', 'Incendio', 'Ricorso Terzi da Incendio']
    }
    if (dc.pacchetto_autocarro === 'EQUILIBRATO') {
      return ['Furto', 'Incendio', 'Atti vandalici', 'Eventi naturali', 'Ricorso Terzi da Incendio']
    }
    if (dc.pacchetto_autocarro === 'COMPLETO') {
      return ['Furto', 'Incendio', 'Atti vandalici', 'Eventi naturali', 'Ricorso Terzi da Incendio', `Cristalli ${dc.cristalli_massimale || ''} euro`.trim()]
    }
  }
  const isPlus = dc.pacchetto === 'PLUS' || dc.pacchetto === 'ADVANCED'
  const out = []

  const base = GARANZIE_BASE_PER_COMPAGNIA[cod]
  if (base) {
    out.push(...base)
    if (isPlus) out.push(...(GARANZIE_PLUS_EXTRA_PER_COMPAGNIA[cod] || []))
  } else {
    // Fallback generico per compagnie non mappate
    let voci = [...VOCI_BASE]
    if (isPlus) voci = [...voci, ...VOCI_PLUS_EXTRA]
    for (const v of voci) out.push(FATTORE_LABEL[v] || VOCI_LABEL[v] || v)
  }

  const sel = data.standalone_selezionati || []
  const prodotti = tariffe?.standalone || []
  for (const s of sel) {
    const p = prodotti.find(x => x.codice === s.codice)
    out.push(p?.nome || s.codice)
  }
  return out
}

function disegnaDettaglioPremio(doc, data, compagniaScelta, tariffe, y, ctx) {
  y = sezione(doc, 'Dettaglio premio', y, ctx)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(...COLORS.text)
  const nomeCompagnia = data.tipo_operazione === 'autocarri_intermediari'
    ? 'Wakam'
    : val(compagniaScelta?.nome) + (compagniaScelta?.codice ? ` (${compagniaScelta.codice})` : '')
  doc.text(`Compagnia: ${nomeCompagnia}`, MARGIN + 6, y)
  y += 6

  const garanzie = elencoGaranzie(data, tariffe)
  if (garanzie.length) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(...COLORS.muted)
    doc.text('Garanzie incluse:', MARGIN + 6, y)
    y += 5

    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.text)
    for (const g of garanzie) {
      y = ensureSpace(doc, y, 6, ctx)
      doc.text(`•  ${g}`, MARGIN + 10, y)
      y += 5
    }
    y += 2
  }

  return y + 2
}

function tabellaStandalone(doc, data, tariffe, startY, ctx) {
  const sel = data.standalone_selezionati || []
  if (!sel.length) return startY
  const prodotti = tariffe?.standalone || []
  const rows = sel.map(s => {
    const p = prodotti.find(x => x.codice === s.codice)
    const prezzo = parseFloat(p?.prezzo_annuo || 0)
    const tot = prezzo * (s.anni || 1)
    return [val(p?.nome || s.codice), `${s.anni || 1}`, formatEuro(prezzo), formatEuro(tot)]
  })

  startY = ensureSpace(doc, startY, rows.length * 7 + 14, ctx)
  autoTable(doc, {
    startY,
    head: [['Prodotto', 'Anni', 'Prezzo/anno', 'Totale']],
    body: rows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: [255, 255, 255], fontSize: 10 },
    styles: {
      font: 'helvetica', fontSize: 9.5, textColor: COLORS.text,
      cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
    },
    columnStyles: {
      1: { halign: 'right', cellWidth: 20 },
      2: { halign: 'right', cellWidth: 35 },
      3: { halign: 'right', cellWidth: 35 },
    },
    margin: { left: MARGIN + 6, right: MARGIN },
  })
  return doc.lastAutoTable.finalY + 4
}

// Genera e scarica il PDF riassuntivo della pratica.
// Parametri:
//   data            → state pratica (cliente_nome, dati_comuni, preventivi, ecc.)
//   profile         → profilo dealer/subagente loggato (per nominativo+email)
//   compagniaScelta → oggetto compagnia (per nome leggibile)
//   tariffe         → necessarie solo per la sezione stand-alone (nomi prodotti)
//   numeroPratica   → string codice pratica (YYYYMMDD-XXXXXX)
export async function generaPdfPratica({ data, profile, compagniaScelta, tariffe, numeroPratica }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const logoDataUrl = await caricaLogoDataUrl()
  const ctx = { logoDataUrl, numeroPratica }

  disegnaHeader(doc, logoDataUrl, numeroPratica)

  let y = 48

  // Dealer / agente (no ruolo per richiesta committente)
  y = sezione(doc, 'Pratica gestita da', y, ctx)
  y = tabellaCoppie(doc, [
    ['Nominativo', val(profile?.nome || profile?.email)],
    ['Email', val(profile?.email)],
  ], y, ctx)

  // Cliente
  y = sezione(doc, 'Contraente', y, ctx)
  y = tabellaCoppie(doc, rigaCliente(data), y, ctx)

  // Veicolo (sempre presente: targa/marca/modello in primo piano)
  y = sezione(doc, 'Veicolo', y, ctx)
  y = tabellaCoppie(doc, rigaVeicolo(data), y, ctx)

  const isSoloSA = data.tipo_operazione === 'solo_standalone'

  // Polizza (solo se non è solo_standalone)
  if (!isSoloSA) {
    y = sezione(doc, 'Polizza CVT', y, ctx)
    y = tabellaCoppie(doc, rigaPolizza(data), y, ctx)
  } else {
    y = sezione(doc, 'Tipo operazione', y, ctx)
    y = tabellaCoppie(doc, [['Tipo', TIPO_OP_LABEL.solo_standalone]], y, ctx)
  }

  // Dettaglio premio compatto (solo polizza): lista fattori, niente costi singoli,
  // niente broker fee, niente commissione dealer.
  if (!isSoloSA && data.scelta_compagnia) {
    y = disegnaDettaglioPremio(doc, data, compagniaScelta, tariffe, y, ctx)
  }

  // Stand-alone
  const standalone = data.standalone_selezionati || []
  if (standalone.length > 0) {
    y = sezione(doc, 'Garanzie Stand-alone', y, ctx)
    y = tabellaStandalone(doc, data, tariffe, y, ctx)
  }

  // Totale finale evidenziato (UNICA voce di totale nel PDF)
  y = ensureSpace(doc, y, 20, ctx)
  doc.setFillColor(...COLORS.primary)
  doc.roundedRect(MARGIN, y, doc.internal.pageSize.getWidth() - 2 * MARGIN, 14, 2, 2, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('TOTALE PRATICA', MARGIN + 6, y + 9)
  doc.setFontSize(14)
  doc.text(formatEuro(data.totale_finale), doc.internal.pageSize.getWidth() - MARGIN - 4, y + 9, { align: 'right' })
  y += 20

  // Scadenza preventivo + Data di decorrenza polizza (PRIMA della normativa privacy)
  y = sezione(doc, 'Validità preventivo', y, ctx)
  y = ensureSpace(doc, y, 18, ctx)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9.5)
  doc.setTextColor(...COLORS.muted)
  const validitaTxt = 'La presente quotazione è valida 30 giorni a partire dalla data di immissione del preventivo.'
  const splitVal = doc.splitTextToSize(validitaTxt, doc.internal.pageSize.getWidth() - 2 * MARGIN - 6)
  doc.text(splitVal, MARGIN + 6, y)
  y += splitVal.length * 4 + 3
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(...COLORS.text)
  doc.text(`Scadenza preventivo: ${formatDecorrenza(data.created_at)}`, MARGIN + 6, y)
  y += 6
  doc.text(`Data di decorrenza polizza: ${fmtDate((data.dati_comuni || {}).data_decorrenza)}`, MARGIN + 6, y)
  y += 8

  // Privacy / consenso
  const dc = data.dati_comuni || {}
  if (dc.consenso_privacy) {
    y = sezione(doc, 'Consenso privacy', y, ctx)
    y = ensureSpace(doc, y, 20, ctx)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...COLORS.muted)
    const txt = `Il contraente ha confermato di aver letto e accettato l'informativa privacy in data ${fmtDateTime(dc.consenso_privacy_at)} (versione ${val(dc.consenso_privacy_version)}). Base giuridica: esecuzione del contratto assicurativo (Art. 6.1.b GDPR).`
    const split = doc.splitTextToSize(txt, doc.internal.pageSize.getWidth() - 2 * MARGIN - 6)
    doc.text(split, MARGIN + 6, y)
    y += split.length * 4 + 2
  }

  // Firma del contraente per presa visione (DOPO l'informativa privacy)
  y = sezione(doc, 'Firma del contraente', y, ctx)
  y = ensureSpace(doc, y, 30, ctx)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...COLORS.muted)
  const presaVisione = 'Il contraente dichiara di aver preso visione dell\'informativa privacy, della presente quotazione e della data di decorrenza.'
  const splitPV = doc.splitTextToSize(presaVisione, doc.internal.pageSize.getWidth() - 2 * MARGIN - 6)
  doc.text(splitPV, MARGIN + 6, y)
  y += splitPV.length * 4 + 12

  const pageWidthSig = doc.internal.pageSize.getWidth()
  doc.setDrawColor(...COLORS.border)
  doc.setLineWidth(0.3)
  doc.line(MARGIN + 6, y, MARGIN + 56, y)                                  // riga data
  doc.line(pageWidthSig - MARGIN - 85, y, pageWidthSig - MARGIN, y)        // riga firma
  doc.setFontSize(8)
  doc.setTextColor(...COLORS.muted)
  doc.text('Data', MARGIN + 6, y + 4)
  doc.text('Firma per presa visione', pageWidthSig - MARGIN - 85, y + 4)
  y += 10

  disegnaFooter(doc)

  const safeNum = (numeroPratica || `pratica-${Date.now()}`).replace(/[^a-zA-Z0-9-_]/g, '_')
  doc.save(`Pratica_${safeNum}.pdf`)
}
